// Custom DS-Table column rendering the Opportunity Status as a design-system Pill (the public column API
// has no arbitrary-JSX cell, so we author a column following the established DS pattern — see
// ColoredIdIconColumn — and import the two cell/column-support helpers from the dist internals, the
// accepted web-front convention for custom columns).
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Pill, PillVariant} from '@forecast-it/design-system';
import Styled from 'styled-components';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunityState, OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import {UnconvertedHelp} from './UnconvertedHelp';

export interface StatusPillColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	/**
	 * Localized explanation of what "Unconverted" means (002 D9). Shown from a help affordance beside
	 * that pill only.
	 */
	unconvertedHelp: string;
	/** Short localized name for the help affordance itself (not the explanation it reveals). */
	unconvertedHelpLabel: string;
	width?: string;
	// px: the default width (and what a grip double-click resets to) and the drag lower bound.
	size?: number;
	minSize?: number;
}

const Row = Styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;
// All Opportunity Status pills use the neutral chip background (the design-system chipsStatus Neutral token,
// #F5F3F9); the status is conveyed by the label text, not the pill colour (per the design).
const STATUS_PILL_VARIANT: Record<OpportunityState, PillVariant> = {
	open: PillVariant.NEUTRAL,
	won: PillVariant.NEUTRAL,
	lost: PillVariant.NEUTRAL,
	won_unconverted: PillVariant.NEUTRAL,
};

export function statusPillVariant(state: OpportunityState): PillVariant {
	return STATUS_PILL_VARIANT[state];
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StatusPillColumn = (_: StatusPillColumnType): null => null;

StatusPillColumn.toColumnDef = (child: React.ReactElement<StatusPillColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {statusState, statusLabel} = row.original;
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-status-cell">
				{statusState ? (
					<Row>
						<span data-cy="status-pill">
							<Pill variant={statusPillVariant(statusState)}>{statusLabel}</Pill>
						</span>
						{statusState === 'won_unconverted' && (
							<UnconvertedHelp
								help={child.props.unconvertedHelp}
								triggerLabel={child.props.unconvertedHelpLabel}
								dataCy="opportunity-unconverted-info"
								focusable
							/>
						)}
					</Row>
				) : null}
			</StyledCell>
		);
	},
});

export default StatusPillColumn;
